'use client'
import {ReactNode} from 'react'
import {useEscapeKey, useFocusTrap} from '@local/hooks'

import {cx} from '@local/utils/cx'
import {Button} from '@local/ui/components'

export interface ModalProps {
	containerClassName?: string;
	backdropClassName?: string;
	className?: string;
	isActive: boolean;
	children: ReactNode;
	onClickClose: (args) => void;
	backdropTransparent?: boolean;
	innerRef?;
}

export const Modal = ({
	children,
	containerClassName,
	backdropClassName,
	className,
	isActive = false,
	onClickClose,
	backdropTransparent = false,
	innerRef = null,
}: ModalProps) => {
	const trapRef = useFocusTrap({
		includeContainer: true,
		initialFocus: 'container',
		returnFocus: true,
		updateNodes: true,
		isActive,
		onClickClose,
	})

	useEscapeKey(onClickClose)

	return (
		<div
			className={cx(
				'fixed z-50 inset-0 h-screen',
				isActive ? 'visible' : 'invisible',
				containerClassName,
			)}
			aria-modal
			aria-hidden={!isActive}
			ref={innerRef}>

			<div
				onClick={onClickClose}
				className={cx(
					'absolute inset-0 overflow-x-clip overflow-y-auto',
					'transition-[visibility,background-color] duration-500 ease-cubic-inout',
					isActive ? 'visible bg-black/30 ease-expo-out' : 'invisible bg-black/0',
					backdropTransparent && isActive && 'bg-black/0',
					backdropClassName,
				)} />
			<div
				className={cx(
					'absolute bg-white overscroll-contain w-fit',
					'left-1/2 -translate-x-1/2',
					'lg:top-1/2 lg:-translate-y-1/2',
					'transform-gpu transition-[transform,opacity,visibility] duration-500 ease-cubic-inout',
					isActive ? 'visible lg-max:translate-y-0 lg:scale-100 ease-expo-out lg:opacity-100' : 'invisible lg-max:translate-y-[120%] lg:scale-90 lg:opacity-0',
					className,
				)}
				ref={trapRef}>
				{children}
			</div>
		</div>
	)
}
